import { SettingDataType } from "@store/state-types";
import { EMPTY_BILL_ERROR_CODE, MAX_TIP_AMOUNT_0_CASE, ROUNDING_RULE, SPLIT_THRESHOLD } from "../data/constants";

export const get10xAmount = (amount:number) => {
	return ((10 * amount) || MAX_TIP_AMOUNT_0_CASE)
}

// Redirect target read from storage or the URL (e.g. swURL from ?swOriginUrl= on scan). Only same-site paths and
// http(s) URLs pass, so a crafted link can't run script through javascript: or data: URLs. Anything else goes to '/'.
export const toSafeRedirectUrl = (url: string | null | undefined): string => {
	if (!url) return '/';
	if (url.startsWith('/') && !url.startsWith('//')) return url;
	try {
		const { protocol } = new URL(url);
		return protocol === 'http:' || protocol === 'https:' ? url : '/';
	} catch {
		return '/';
	}
}

// True when a bill API error carries the backend empty-bill code (SMTWB-9198). The body is plain text
// by default, or { message } when the request sends Accept: application/json.
export const isEmptyBillError = (error: any): boolean => {
	const data = error?.response?.data;
	return data === EMPTY_BILL_ERROR_CODE || data?.message === EMPTY_BILL_ERROR_CODE;
}

// Reconstructs the shareable bill entry URL (no query params) so another payer can open the
// same bill and pay their share. Uses the merchant scan route from route-manager. referenceId
// is the pos session id (see getLoggerMetadataFromSettings).
export const buildShareBillLink = (settings: SettingDataType | null): string => {
	const merchantKey = settings?.merchantKey;
	const tableNo = settings?.tableNo;
	const referenceId = settings?.posSessionId;
	if (!merchantKey || !tableNo) return window.location.origin;
	const base = `${window.location.origin}/merchant/${encodeURIComponent(merchantKey)}/table/${encodeURIComponent(tableNo)}`;
	return referenceId ? `${base}/reference_id/${encodeURIComponent(referenceId)}` : base;
}

// Copies text to the clipboard. Uses the async Clipboard API when available (secure context),
// with a legacy execCommand fallback for non-secure contexts. Returns success as a boolean.
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
	try {
		if (navigator?.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch (e) {
		// fall through to legacy path
	}
	try {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.style.position = 'fixed';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.focus();
		textarea.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(textarea);
		return ok;
	} catch (e) {
		return false;
	}
}

export const getThemeValue = (prefix:string,suffix:string,css:string):string =>{
    var i = css.indexOf(prefix);
	if (i >= 0) {
		css = css.substring(i + prefix.length);
	}
	else {
		return '';
	}
	if (suffix) {
		i = css.indexOf(suffix);
		if (i >= 0) {
			css = css.substring(0, i);
		}
		else {
		  return '';
		}
	}
	return css;
}

export const hexToRGB = (hex:string)=>{
    if(hex?.length !== 7){
        return false;
    }
    let aRgbHex:any = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
    return `${parseInt(aRgbHex[1], 16)},${parseInt(aRgbHex[2], 16)},${parseInt(aRgbHex[3], 16)}`;
}

export const connectionImage = () =>{
	return "data:image/svg+xml;base64,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"
}

export function hundredRounding(value: number, additionalAmount:number): number {
	return parseFloat((value + additionalAmount).toFixed(2));
}

export function ntdRounding(value: number, additionalAmount:number): number {
	return parseFloat((value + additionalAmount).toFixed(0));
}

export function doRounding(amount:number, setting:SettingDataType){
	if(setting.roundingRule.code === ROUNDING_RULE.NTD) return ntdRounding(amount, setting.roundingRule.additionalAmount);
	return hundredRounding(amount, setting.roundingRule.additionalAmount);
}

export function calculateSplit(balanceAmount: number, noOfPeopleOnTbl: number, noOfPeoplePayingFor: number, setting: SettingDataType){
	const amountPayable = doRounding((balanceAmount / (noOfPeopleOnTbl) * noOfPeoplePayingFor), setting);
	const amountPerPax = doRounding((balanceAmount / (noOfPeopleOnTbl)), setting);
    const remainder = doRounding(balanceAmount - (amountPayable + (amountPerPax * (noOfPeopleOnTbl - noOfPeoplePayingFor))), setting);
    return {
        amountPayable: amountPayable + (remainder > 0 ? remainder : 0),
        amountPerPax
    }
}

export function isSplitValid(balanceAmount: number,amount: number, setting: SettingDataType) {
	if((doRounding((balanceAmount - amount), setting) > SPLIT_THRESHOLD) && amount > 0.01) return true
	return false;
}

export const isSplitInitiated = (billData:any) => {
    if(billData?.paymentInfo?.splitBillInfo?.type) return true;
    else return false;
}

export const getEnv = () => {
	const currentHost = window.location.hostname || "";
	
	const stagingKeywords = ["stg", "stage", "127.0.0.1", "localhost"];
	
	if (stagingKeywords.some(keyword => currentHost.includes(keyword))) {
	  return 'staging';
	}
	
	if (currentHost.includes("uat")) {
	  return 'uat';
	}
	
	return 'production';
  }
  
  export const getCookie = (name: string): string | null => {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : null;
}

export const getLoggerMetadataFromSettings = (settings: SettingDataType | null) => {
	return {
		merchantKey: settings?.merchantKey,
		tableNo: settings?.tableNo,
		referenceId: settings?.posSessionId,
		spSessionId: settings?.xSpSessionID
	}
  }
